import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink, Calendar, MapPin, GraduationCap, Users, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/seo/SEOHead';
import { distributor } from '../data/mock';
import { useMotion } from '../context/MotionContext';
import { trackCTAClick } from '../utils/analytics';
import GlitchText from '../components/ui/GlitchText';

const API = process.env.REACT_APP_BACKEND_URL;

const EventsPage = () => {
  const { prefersReducedMotion } = useMotion();
  const [tradeFairs, setTradeFairs] = useState([]);
  const [course, setCourse] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/events`);
        const data = await res.json();
        if (!active) return;
        setTradeFairs(Array.isArray(data.tradeFairs) ? data.tradeFairs : []);
        setCourse(data.course || null);
        setLastUpdated(data.lastUpdated || null);
      } catch (e) {
        if (active) { setTradeFairs([]); setCourse(null); }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleFMSClick = () => trackCTAClick('fms', 'EventsPage');

  return (
    <>
      <SEOHead
        title="Events"
        description="XION Medical events, trade fairs and training — auto-synced from xion-medical.com. Meet XION worldwide or attend endoscopy training in the Philippines via FMSInc."
        path="/events"
      />
      <Header />
      <main className="pt-32 min-h-screen bg-black grid-bg" data-testid="events-page">
        <div className="max-w-7xl mx-auto px-6 py-16">
          {/* Page Header */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <p className="terminal-label text-[#00FFD1] mb-4">// Events &amp; Training</p>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {prefersReducedMotion ? 'Events' : <GlitchText text="Events" />}
            </h1>
            <p className="text-white/60 text-lg max-w-2xl">
              Meet the XION team at international trade fairs and congresses, or attend specialised
              endoscopy training programs. Reach out to {distributor.shortName} for Philippine participation.
            </p>
          </motion.div>

          {/* Trade Fairs Section */}
          <section className="mb-16">
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex flex-wrap items-end justify-between gap-4 mb-8"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 border border-[#00FFD1]/30 flex items-center justify-center">
                  <Users className="text-[#00FFD1]" size={28} />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white">Trade Fairs &amp; Conventions</h2>
                  <p className="text-white/60">Meet us at leading medical exhibitions worldwide.</p>
                </div>
              </div>
              <span className="flex items-center gap-1.5 text-white/40 text-xs font-mono" data-testid="events-sync-note">
                <RefreshCw size={12} className="text-[#00FFD1]" />
                Auto-synced from xion-medical.com{lastUpdated ? ` · ${new Date(lastUpdated).toLocaleDateString()}` : ''}
              </span>
            </motion.div>

            {loading ? (
              <div className="grid md:grid-cols-2 gap-6" data-testid="events-loading">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="card-dark p-6 animate-pulse h-40">
                    <div className="h-3 w-24 bg-white/10 rounded mb-4" />
                    <div className="h-4 w-3/4 bg-white/10 rounded mb-3" />
                    <div className="h-3 w-1/2 bg-white/10 rounded" />
                  </div>
                ))}
              </div>
            ) : tradeFairs.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6" data-testid="events-list">
                {tradeFairs.map((event, index) => (
                  <motion.div
                    key={index}
                    initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: Math.min(index * 0.06, 0.4) }}
                    className="card-dark p-6 group flex flex-col"
                    data-testid={`event-card-${index}`}
                  >
                    <div className="flex items-center gap-2 text-[#00FFD1] text-xs font-mono mb-3">
                      <Calendar size={14} /> {event.date}
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#00FFD1] transition-colors">
                      {event.title}
                    </h3>
                    {event.venue && (
                      <p className="flex items-start gap-1.5 text-white/55 text-sm mb-3">
                        <MapPin size={14} className="mt-0.5 shrink-0 text-white/40" />
                        {event.venue}
                      </p>
                    )}
                    {event.description && (
                      <p className="text-white/50 text-sm mb-4">{event.description}</p>
                    )}
                    <div className="mt-auto flex flex-wrap items-center gap-5 pt-2">
                      <Link
                        to="/contact-ph"
                        className="text-[#00FFD1] text-sm font-mono hover:underline flex items-center gap-1"
                        data-testid={`event-ph-info-${index}`}
                      >
                        Request PH Info <ArrowRight size={14} />
                      </Link>
                      {event.infoUrl && (
                        <a
                          href={event.infoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white/55 text-sm font-mono hover:text-white flex items-center gap-1"
                          data-testid={`event-site-${index}`}
                        >
                          Event site <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="card-dark p-8 text-center" data-testid="events-empty">
                <p className="text-white/60">The event schedule is being updated. Please contact {distributor.shortName} for the latest dates.</p>
              </div>
            )}
          </section>

          {/* Course (Lübbenau) Section */}
          {course && course.body && (
            <section className="mb-16">
              <motion.div
                initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex items-center gap-4 mb-8"
              >
                <div className="w-14 h-14 border border-[#00FFD1]/30 flex items-center justify-center">
                  <GraduationCap className="text-[#00FFD1]" size={28} />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white">Training &amp; Courses</h2>
                  <p className="text-white/60">Specialised endoscopy &amp; phoniatrics courses by XION.</p>
                </div>
              </motion.div>

              <motion.div
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="card-dark p-6 md:p-8"
                data-testid="events-course"
              >
                <span className="inline-block px-2 py-1 bg-[#00FFD1]/10 border border-[#00FFD1]/30 text-[#00FFD1] text-xs font-mono">
                  Annual Course
                </span>
                <h3 className="text-2xl font-semibold text-white mt-3 mb-4">{course.title}</h3>
                {course.body.split('\n\n').map((para, i) => (
                  <p key={i} className="text-white/60 leading-relaxed mb-3">{para}</p>
                ))}
                <Link to="/contact-ph" className="btn-primary mt-3" data-testid="course-ph-info">
                  Request PH Info
                  <ArrowRight size={16} />
                </Link>
              </motion.div>
            </section>
          )}

          {/* PH Training Notice */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-[#00FFD1]/10 to-transparent border border-[#00FFD1]/30 p-8 md:p-12"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="terminal-label text-[#00FFD1] mb-3">// Philippines Training</p>
                <h3 className="text-2xl font-bold text-white mb-4">Training in the Philippines</h3>
                <p className="text-white/60 mb-6">
                  {distributor.shortName} offers regular training sessions for healthcare professionals
                  on XION endoscopy systems. Get hands-on experience and certification from XION-trained experts.
                </p>
                <ul className="space-y-2 text-white/50 text-sm">
                  <li className="flex items-center gap-2">
                    <Calendar size={14} className="text-[#00FFD1]" />
                    Quarterly training sessions
                  </li>
                  <li className="flex items-center gap-2">
                    <MapPin size={14} className="text-[#00FFD1]" />
                    FMSInc. Training Center
                  </li>
                </ul>
              </div>
              <div className="flex flex-col gap-4">
                <Link to="/contact-ph" className="btn-primary w-full justify-center">
                  Request Training Info
                  <ArrowRight size={16} />
                </Link>
                <a
                  href={distributor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleFMSClick}
                  className="btn-secondary w-full justify-center"
                >
                  Contact FMSInc.
                  <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default EventsPage;
